import { useAppState } from 'contexts/AppContext';
import FHIRFetcher, { MultiFetcher } from 'data/FHIRFetcher';
import type { Bundle, BundleLink, CareTeam, Patient, Task } from 'fhir/r4';
import { ACTIVITY_TAB } from 'fhir-sdoh/common/constants';
import _ from 'lodash';
import moment from 'moment';
import { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import type { ForwardedRef } from 'react';
import { Grid, Loader, Segment } from 'semantic-ui-react';
import useSWR from 'swr';
import type { FlatPractitioner, Option, ReferralSummary } from 'types';
import * as FHIRUtils from 'utils/FHIRUtils';
import * as Helpers from 'utils/Helpers';
import type { ResourceEntry } from 'utils/bundleNarrowing';

import TaskFilters from './filters';
import NewTaskList from './newTaskList';
import TaskDetail from './taskDetails';
import TaskHeader from './taskHeader';

/**
 * What `MultiFetcher` resolves to: one bundle per requested URL, or `void` when it
 * was handed no URLs at all. Derived from the fetcher rather than restated, so the
 * two cannot drift.
 */
type MultiFetchResult = Awaited<ReturnType<typeof MultiFetcher<Bundle>>>;

/**
 * The task-list filter selections. Every field is a fragment of the `/Task` query
 * string (or null when that filter is off); `program` carries the selected payer ids.
 *
 * The index signature is what `./filters` needs: that modal writes through a computed
 * `[name]` key taken from each control's `name` prop, so it cannot see the named
 * fields alone. Both sides therefore agree on one type instead of two structurally
 * different ones.
 */
export type TaskFilterState = {
  [key: string]: string | string[] | null | undefined;
  category: string | null;
  dateStatus: string | null;
  period: string | null;
  practitioner: string | null;
  priority: string | null;
  program: string[];
  referral: string | null;
  status: string | null;
  subCategory: string | null;
  vendor: string | null;
};

/**
 * What the task list accepts. Rendered from the three activity tabs in
 * `components/home/activity` and from the per-member task panel in
 * `components/patient/careplan`.
 *
 * Several fields are `false | X`: the activity tabs pass
 * `data && !error && data`, whose falsy arm is `false` rather than `undefined`.
 */
type TaskCardProps = {
  PractitionerData?: ResourceEntry<'Practitioner'>[] | false | null | undefined;
  /** Which activity tab is mounted; keys the saved-filter storage. */
  activeitem?: string | undefined;
  /** True on the activity tabs, false on the per-member panel. */
  activity?: boolean | undefined;
  allReferralData?: ReferralSummary[] | undefined;
  /** Passed by `components/patient/careplan` but never read here. */
  communicationsMutate?: (() => void | Promise<unknown>) | undefined;
  fhirPatient?: Patient | undefined;
  isCareTeamTasks?: boolean | undefined;
  onPatientSelected?: ((patientData: unknown) => void) | undefined;
  orgId?: string | undefined;
  patientId?: string | undefined;
  /** SWR mutate for the patient shell's `/Task?patient=…`; kept in sync on edits. */
  patientTasksMutate?: (() => void | Promise<unknown>) | undefined;
  /** Gates the practitioner filter; off on the per-member panel. */
  practfilter?: boolean | undefined;
  practitionerId?: string | undefined;
  selectedPayer?: string | undefined;
};

export type TaskCardHandle = {
  refreshTasks: () => void;
  loadData: () => void;
};

const TaskCard = forwardRef((props: TaskCardProps, ref: ForwardedRef<unknown>) => {
  const { authState, appState: authInfo } = useAppState();
  const [state, setState] = useState<TaskFilterState>({
    category: null,
    period: null,
    status: null,
    priority: null,
    practitioner: null,
    vendor: null,
    referral: null,
    subCategory: null,
    dateStatus: null,
    program: [],
  });

  const fhirPractitioners = props.PractitionerData;
  const [taskList, setTaskList] = useState<fhir4.Task[] | null>(null);
  const [currentTaskId, setCurrentTaskId] = useState<string | null>(null); // For highlighting task in list
  // `handleNewTask` sets the id of a freshly created task; the sidebar close resets
  // it to `null`.
  const [newTaskId, setNewTaskId] = useState<string | null>(null);
  const [sidebarVisible, setSidebarVisible] = useState(false);
  // `resetFilters` here and `resetTempFiltersFunc` in `./filters` both clear this to
  // `null` rather than `[]`; the save path in `./filters` reads it back through a
  // `? :` that treats null and empty alike.
  const [datevalue, setDateValue] = useState<Date[] | null>([]);
  const [loader, setLoader] = useState(true);
  const [selectedTasks, setSelectedTasks] = useState<fhir4.Task[]>([]);
  const [selectedCareplanTasks, setSelectedCareplanTasks] = useState(false); //use to determine and track if a careplan task is selected in multi-select
  const [render, setRender] = useState(false);
  const [referralIdList, setReferralIdList] = useState<ReferralSummary[]>([]);
  const [nextPage, setNextPage] = useState(false);
  // Written from `splitURL[1]` — the segment after `&_page_token=` — which is
  // `undefined` when the `next` link carries no page token, and reset to `null`
  // when there is no next page. Both absences reach the `nextPage && nextPageURL`
  // guard at the call site as falsy.
  const [nextPageURL, setNextPageURL] = useState<string | null | undefined>(null);
  const [renderURL, setRenderURL] = useState(true);
  // Holds the previous paging URL, or `null` when the current request is not a
  // continuation. Written only; the read side is unused, hence the underscore.
  const [_previousURL, setPreviousURL] = useState<string | null>(null);
  const [vendorList, setVendorList] = useState<Option[]>([]);

  const [isTaskFilterOpen, setIsTaskFilterOpen] = useState<boolean>(false);
  // originalTaskList is used to get the original Task List without filtering the subCategory
  const [originalTaskList, setOriginalTaskList] = useState<fhir4.Task[] | null>(null);
  const [myPatientIds, setMyPatientIds] = useState<string[]>([]);
  const [disableMultiSelect, setDisableMultiSelect] = useState(false); // Disable multi select tasks when none selected and orgId not ALL
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);

  // filter State per tab
  // check if saved filter has been fetched from session storage to avoid multiple renders
  const [_tabFilters, setTabFilters] = useState({
    'All Tasks': { fetched: false },
    'My CareTeam Tasks': { fetched: false },
    'My Tasks': { fetched: false },
  });

  const activityBool = props.activity || false;

  useEffect(() => {
    if (window.location.search) {
      const taskId = window.location.search?.split('=')?.[1];
      if (taskId) {
        setCurrentTaskId(taskId);
      }
    }
  }, [window.location.search]);

  useEffect(() => {
    if (authInfo.vendors) {
      setVendorList(authInfo.vendors);
    }
  }, [authInfo]);

  useEffect(() => {
    // `undefined > 0` is false, so an absent list reads as "no referrals" — the
    // answer the original relied on. `?? 0` states that without changing it.
    setReferralIdList((props.allReferralData?.length ?? 0) > 0 ? props.allReferralData! : []);
  }, [props?.allReferralData]);

  useEffect(() => {
    setDisableMultiSelect(selectedTasks.length < 1);
    if (selectedTasks.length > 0) {
      let selectedCareplanTasksExists = false;
      for (let i = 0; i < selectedTasks.length; i++) {
        const currentTask = selectedTasks[i];
        if (currentTask?.code?.coding?.[0]?.code === 'careplan') {
          selectedCareplanTasksExists = true;
          break;
        }
      }
      //set selectedCareplanTasks to true so change assginee is disabled
      setSelectedCareplanTasks(selectedCareplanTasksExists);
    } else {
      setSelectedCareplanTasks(false);
    }
  }, [taskList, selectedTasks]);

  useEffect(() => {
    setRenderURL(true);

    // Filter by subCategory if selected
    if (state.subCategory) {
      setLoader(false);
      if (!originalTaskList) {
        setOriginalTaskList(taskList);
      }

      const sourceList = originalTaskList ?? taskList;
      if (sourceList) {
        const filteredTaskList = sourceList.filter(
          (task) => task.reasonCode && task.reasonCode?.text === state.subCategory
        );
        setTaskList(filteredTaskList);
      } else {
        setTaskList(null);
      }
    } else if (originalTaskList) {
      setTaskList(originalTaskList);
    }
  }, [state, props.orgId]);

  useEffect(() => {
    if (initialLoadComplete && loader) {
      // Add a small delay to allow rendering to complete
      const timer = setTimeout(() => {
        setLoader(false);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [initialLoadComplete, loader]);

  useEffect(() => {
    if ((props.allReferralData?.length ?? 0) > 0) {
      setReferralIdList(props.allReferralData!);
    } else {
      setReferralIdList([]);
    }
  }, [props?.allReferralData]);

  useEffect(() => {
    setVendorList(authInfo.vendors);
  }, [authInfo]);

  useEffect(() => {
    //only show saved filter when we first log into CB
    //setting each activity tabFilters to true if the filter has been retrived
    if (props?.activeitem) {
      const key = `fetchedFilter:${props.activeitem}`;
      const hasFetched = sessionStorage.getItem(key);

      if (!hasFetched) {
        retriveFilterFromLocalStorage();
        setTabFilters((prev) => ({
          ...prev,
          // Guarded by the `if (props?.activeitem)` above, which TypeScript does not
          // carry into the setter callback.
          [props.activeitem!]: {
            fetched: true,
          },
        }));

        sessionStorage.setItem(key, 'true');
      }
    }
  }, [props?.activeitem]);

  useEffect(() => {
    //set to empty filter state if specific program is selected
    if (props.selectedPayer !== 'All') {
      setState({
        category: null,
        period: null,
        status: null,
        priority: null,
        practitioner: null,
        vendor: null,
        referral: null,
        subCategory: null,
        dateStatus: null,
        program: [],
      });
      setDateValue([]);
    }
  }, [props.selectedPayer]);

  const sliceIntoChunks = function <T>(arr: T[], chunkSize: number) {
    const res: T[][] = [];
    for (let i = 0; i < arr.length; i += chunkSize) {
      const chunk = arr.slice(i, i + chunkSize);
      res.push(chunk);
    }
    return res;
  };

  const retriveFilterFromLocalStorage = () => {
    let savedStateRaw: string | null = null;
    let savedDateState: string | null = null;

    //retrive saved filter from local stroage
    if (props.activeitem === ACTIVITY_TAB.ALL_TASKS) {
      savedStateRaw = localStorage.getItem('allFilter');
      savedDateState = localStorage.getItem('allDateFilter');
    } else if (props.activeitem === ACTIVITY_TAB.MY_CARETEAM_TASKS) {
      savedStateRaw = localStorage.getItem('careTeamFilter');
      savedDateState = localStorage.getItem('careTeamDateFilter');
    } else if (props.activeitem === ACTIVITY_TAB.MY_TASKS) {
      savedStateRaw = localStorage.getItem('myFilter');
      savedDateState = localStorage.getItem('myDateFilter');
    }

    if (savedStateRaw || savedDateState) {
      /*
       * The only writer of these keys is `saveFilters` in `./filters`, which
       * stringifies the same `TaskFilterState` this component holds; the filter
       * key and its date key are always written together.
       *
       * SUSPECTED DEFECT — REPORTED, NOT FIXED. The guard is `||`, so the block is
       * also entered when only the *date* key is present. `JSON.parse(null)`
       * yields `null` (it stringifies to `"null"`), and `savedState.dateStatus`
       * one line down then throws. The assertion below records the shape the sole
       * writer produces; it does not claim the read succeeded.
       */
      const savedState = JSON.parse(savedStateRaw!) as TaskFilterState;
      //retrive and update the calendar filter if we have date range selected saved
      if (savedDateState) {
        setDateValue((JSON.parse(savedDateState) as string[])?.map((dateStr: string) => new Date(dateStr)));
      } else {
        setDateValue([]);
      }

      //if dateStatus is selected, such as past due, today, future, the period string needs to be updated
      if (savedState.dateStatus) {
        const today = moment().local().format('YYYY-MM-DD');
        if (savedState.dateStatus === 'today') {
          savedState.period = `&period=eq${today}`;
        } else if (savedState.dateStatus === 'pastdue') {
          savedState.period = `&period=lt${today}`;
        } else if (savedState.dateStatus === 'future') {
          savedState.period = `&period=gt${today}`;
        }
      }
      setState(savedState);
      setLoader(true);
      void tasksMutate();
      setRenderURL(true);
    }
  };
  /*
   * Keeps only the LAST occurrence of each task id — later pages carry the more
   * recently updated copy.
   *
   * `Array.prototype.findLastIndex` is ES2023 and this program declares `lib:
   * ES2022`, so the compiler does not see it even though every browser this app
   * targets ships it. The reverse scan below is the same predicate expressed in
   * ES2022: the last index whose id matches, compared against the current index.
   */
  function onlyUnique(value: Task, index: number, array: Task[]) {
    for (let i = array.length - 1; i >= 0; i--) {
      if (array[i]!.id === value.id) return i === index;
    }
    return false;
  }

  const options = {
    shouldRetryOnError: false,
    revalidateOnFocus: false,
    revalidateIfStale: false,
    revalidateOnReconnect: false,
    revalidateOnMount: true,
    onSuccess: (data: MultiFetchResult) => {
      setLoader(true);
      setOriginalTaskList(null);

      if (state.period !== null && !renderURL) {
        setLoader(false);
        return;
      }

      let currentURL: string | null | undefined = null;
      if (state.period) {
        if (endPoints[0]?.includes('&_page_token')) {
          currentURL = endPoints[0]?.split('&_page_token')?.[0];
        } else {
          currentURL = endPoints[0];
        }
      }

      let currentRequest = false;
      if (currentURL && endPoints[0]!.includes('&_page_token') && state.period) {
        currentRequest = true;
        setPreviousURL(currentURL);
      } else {
        setPreviousURL(null);
      }

      /*
       * SUSPECTED DEFECT — REPORTED, NOT FIXED. This paging block reads `link` on
       * the first bundle three times, and only the first read is optional-chained.
       * Once `findIndex` has answered something other than `-1` the author treats
       * `link` as present, but `?.findIndex(...)` yields `undefined` when `link` is
       * absent, and `undefined !== -1` is TRUE — so an absent `link` enters the
       * block and throws on the unguarded `.findIndex` one line down. The guard
       * admits exactly the case it was written to exclude.
       *
       * `Bundle.link` is `0..*` in FHIR R4 and this server omits it on a
       * single-page result, so the block is entered on the last page of any task
       * list; it survives because `state.period !== null` is false on the
       * unfiltered view, which is how the tab is normally opened.
       *
       * `link[nextLinkIndex]` and its `.url` are a further absence: `findIndex`
       * returns `-1` when there is no `next` relation, and `link[-1]` is
       * `undefined`.
       */
      if (state.period !== null && data![0]!.link?.findIndex((obj: BundleLink) => obj.relation === 'next') !== -1) {
        setNextPage(true);
        const nextLinkIndex = data![0]!.link!.findIndex((obj: BundleLink) => obj.relation === 'next');
        const splitURL = data![0]!.link![nextLinkIndex]!.url.split('&_page_token=');
        setNextPageURL(splitURL[1]);
        setRenderURL(true);
      } else {
        setNextPage(false);
        setNextPageURL(null);
        setRenderURL(false);
      }

      /*
       * Every entry of every bundle, NOT filtered by `resource.resourceType`: the
       * keys are `/Task/` searches, but a resourceType-filtering helper would drop
       * an entry whose resource omits the field, which is not what the original
       * unconditional spread did.
       */
      const tasksItems: ResourceEntry<'Task'>[] = [];
      // Kept as an expression statement: an `if` would emit different JavaScript.
      // eslint-disable-next-line @typescript-eslint/no-unused-expressions
      data &&
        data.forEach((bundle) => {
          // Kept as an expression statement: an `if` would emit different JavaScript.
          // eslint-disable-next-line @typescript-eslint/no-unused-expressions
          bundle?.entry && tasksItems.push(...(bundle.entry as ResourceEntry<'Task'>[]));
        });

      const newFetch = tasksItems.map((d) => d.resource);

      if (newFetch && newFetch.length > 0) {
        let temp = newFetch;

        // Apply date filters
        if (state.period) {
          if (state.period.includes('ge') && state.period.includes('le')) {
            // Date range filter
            temp = temp.filter((t) => {
              const taskDate = moment(t.executionPeriod?.end ?? t.executionPeriod?.start).format('YYYY-MM-DD');
              const endDate = moment(state.period!.substring(25, 40)).format('YYYY-MM-DD');
              const startDate = moment(state.period!.substring(10, 20)).format('YYYY-MM-DD');

              return taskDate <= endDate && taskDate >= startDate;
            });
          } else if (state.period.includes('ge')) {
            // Greater than or equal filter
            temp = temp.filter((t) => {
              const taskDate = moment(t.executionPeriod?.end ?? t.executionPeriod?.start).format('YYYY-MM-DD');
              const endDate = moment(state.period!.substring(30, 40)).format('YYYY-MM-DD');
              const startDate = moment(state.period!.substring(10, 20)).format('YYYY-MM-DD');

              return taskDate <= endDate && taskDate >= startDate;
            });
          } else if (state.period.includes('gt')) {
            // Greater than filter
            temp = temp.filter((t) => {
              const taskDate = moment(t.executionPeriod?.end ?? t.executionPeriod?.start).format('YYYY-MM-DD');
              const filterDate = moment(state.period!.substring(10, 20)).format('YYYY-MM-DD');

              return taskDate > filterDate;
            });
          } else if (state.period.includes('lt')) {
            // Less than filter
            temp = temp.filter((t) => {
              const taskDate = moment(t.executionPeriod?.end ?? t.executionPeriod?.start).format('YYYY-MM-DD');
              const filterDate = moment(state.period!.substring(10, 20)).format('YYYY-MM-DD');

              // Check for special outreach task case
              let taskNoshow = false;
              const taskHelper = new Helpers.TaskHelper(t);
              const taskCode = taskHelper.getTaskCode()?.code;
              const taskStatus = taskHelper.getStatus();
              const taskStatusReason = taskHelper.getStatusReason();

              if (taskCode === 'outreach' && taskStatus === 'received' && taskStatusReason) {
                taskNoshow = true;
              }

              return taskDate < filterDate && !taskNoshow;
            });
          } else if (state.period.includes('eq')) {
            // Equals filter
            temp = temp.filter((t) => {
              const taskDate = moment(t.executionPeriod?.end ?? t.executionPeriod?.start).format('YYYY-MM-DD');
              const filterDate = moment(state.period!.substring(10, 20)).format('YYYY-MM-DD');

              return taskDate === filterDate;
            });
          }
        }

        // Apply status filter
        if (state.status) {
          temp = temp.filter((t) => {
            return t.status === state.status;
          });
        } else {
          //filter out entered-in-error and on-hold status tasks by default
          temp = temp.filter((t) => {
            return t.status !== 'entered-in-error' && t.status !== 'on-hold';
          });
        }

        // Apply priority filter
        if (state.priority) {
          temp = temp.filter((t) => {
            if (!t.priority && state.priority === 'routine') {
              return true;
            }
            return t.priority === state.priority;
          });
        }

        // Apply referral filter
        if (state.referral) {
          temp = temp.filter(
            (t) =>
              t.groupIdentifier &&
              t.groupIdentifier.system === 'https://projectwell.io/fhir/identifiers/referral-id' &&
              t.groupIdentifier.value === state.referral
          );
        }

        // Sort tasks by execution date
        temp.sort((a, b) => {
          const aDate = new Date(
            moment(a.executionPeriod?.end ?? a.executionPeriod?.start)
              .utc()
              .format()
          ).toDateString();
          const bDate = new Date(
            moment(b.executionPeriod?.end ?? b.executionPeriod?.start)
              .utc()
              .format()
          ).toDateString();

          return Date.parse(aDate) - Date.parse(bDate);
        });

        if (temp.length > 0) {
          if (!currentRequest) {
            setOriginalTaskList(temp);

            if (state.subCategory) {
              temp = temp.filter((t) => t.reasonCode && t.reasonCode?.text === state.subCategory);
            }

            setTaskList(temp);
          } else {
            if (taskList && state.period && currentRequest === true) {
              const newArray = taskList.concat(temp);
              let filteredArray = newArray.filter(onlyUnique);

              setOriginalTaskList(filteredArray);

              if (state.subCategory) {
                filteredArray = filteredArray.filter((t) => t.reasonCode && t.reasonCode?.text === state.subCategory);
              }

              setTaskList(filteredArray);
            } else {
              setOriginalTaskList(temp);

              if (state.subCategory) {
                temp = temp.filter((t) => t.reasonCode && t.reasonCode?.text === state.subCategory);
              }

              setTaskList(temp);
            }
          }
        } else {
          if (!currentRequest) {
            setTaskList(null);
            setOriginalTaskList(null);
          } else if (!taskList) {
            setTaskList(null);
            setOriginalTaskList(null);
          }
        }

        if (!currentTaskId) {
          setCurrentTaskId(temp.length > 0 ? (temp[0]?.id ?? null) : null);
        } else {
          const stillExists = temp.some((t) => t.id === currentTaskId);
          setCurrentTaskId(stillExists ? currentTaskId : temp[0]?.id || null);
        }
      } else {
        setTaskList(null);
        setOriginalTaskList(null);
      }

      if (newTaskId) {
        setCurrentTaskId(newTaskId);
        setNewTaskId(null);
      }

      setLoader(false);
      setInitialLoadComplete(true);
    },
  };

  useImperativeHandle(ref, () => ({
    refreshTasks() {
      void tasksMutate();
    },
    loadData() {
      setLoader(true);
    },
  }));

  const { isValidating: careTeamDataLoading } = useSWR(
    [
      props.activeitem === ACTIVITY_TAB.MY_CARETEAM_TASKS
        ? `/CareTeam?participant=${authInfo.practitionerId}&_count=1000`
        : null,
      authState.token,
    ],
    FHIRFetcher<fhir4.Bundle>,
    {
      shouldRetryOnError: false,
      revalidateOnFocus: false,
      onSuccess: (data: Bundle | undefined) => {
        setLoader(false);

        /*
         * `data?.entry?.length > 0` chains both levels but compares a
         * possibly-`undefined` `length`; `undefined > 0` is false, which is the
         * "no results" answer intended, so the test is right by accident.
         *
         * SUSPECTED DEFECT — REPORTED, NOT FIXED: `entry.resource` is optional on a
         * FHIR Bundle entry (a search-mode `outcome` entry legitimately carries no
         * resource), and `.subject.reference` is dereferenced twice unguarded, so
         * such an entry throws inside the SWR success callback and the member list
         * silently never populates.
         */
        if (data?.entry && data.entry.length > 0) {
          const patientIds = data.entry.map((entry) => {
            // `resource` is the whole `FhirResource` union here; the key is
            // `/CareTeam?participant=…`, so every entry a search returns is a
            // CareTeam. The two assertions below are the absences the defect
            // note above describes, left exactly as written.
            const resource = entry.resource as CareTeam | undefined;
            const patientRef = resource!.subject!.reference!;
            return patientRef.substring(patientRef.indexOf('/') + 1);
          });

          setMyPatientIds(patientIds);
        }
      },
    }
  );

  let endPoints: string[] = [];

  if (!myPatientIds.length) {
    const tasksUrl =
      `/Task/?code:not=fulfill&_count=1000&_sort=period` + // remove this later. code filter needed only for dev due to old tasks
      (props.activity && !props.isCareTeamTasks && props.practitionerId ? `&owner=` + props.practitionerId : ``) +
      (state.vendor ? `&identifier=https://projectwell.io/fhir/identifiers/vendor-codes|` + state.vendor : ``) + //will check specifacally vendor codes. Needed since PK is a vendor coed and bundle code
      (props.selectedPayer !== 'All' && props.activity
        ? '&based-on=' + props.selectedPayer
        : state.program?.length >= 1
          ? '&based-on=' + state.program.toString()
          : ``) + //if under all organization, we can check if any program filter has been added, if not under all organization, we fetch the selected payor
      (state.practitioner ? `&owner=` + state.practitioner : ``) +
      (props.patientId ? `&patient=` + props.patientId : ``) +
      (state.status ? `&status=` + state.status : `&status:not=cancelled`) +
      (state.priority !== null
        ? state.priority === 'routine'
          ? `&priority:not=asap`
          : `&priority=${state.priority}`
        : '') +
      (state.category
        ? `&code=${state.category}`
        : `&code=touchpoint,survey,food-card,confirm-food-card-balance,other-goods-delivery,food-box,food-recall,food-delivery,education,consultation,careplan,communication,triage,outreach,voicemail,enrollment-action`) +
      (state.period ? (state.period.includes('ge') ? state.period : state.period) : ``) +
      (state.referral ? `&group-identifier=${state.referral}` : ``) +
      (nextPage && nextPageURL ? `&_page_token=${nextPageURL}` : ``);

    endPoints.push(tasksUrl);
  } else {
    const uniquePatients = _.uniq(myPatientIds);
    const patientChunks = sliceIntoChunks(uniquePatients, 200);

    patientChunks.forEach((patientIds) => {
      const url =
        `/Task/?_sort=period&_count=1000&code:not=fulfill` + // remove this later. code filter needed only for dev due to old tasks
        (props.activity && !props.isCareTeamTasks && props.practitionerId ? `&owner=` + props.practitionerId : ``) +
        (props.activity && props.isCareTeamTasks && patientIds?.length > 0 ? `&patient=` + patientIds.join(',') : ``) +
        (state.vendor ? `&identifier=` + state.vendor : ``) +
        (props.selectedPayer !== 'All' && props.activity
          ? '&based-on=' + props.selectedPayer
          : state.program?.length >= 1
            ? '&based-on=' + state.program.toString()
            : ``) + //if under all organization, we can check if any program filter has been added, if not under all organization, we fetch the selected payor
        (state.practitioner ? `&owner=` + state.practitioner : ``) +
        (props.patientId ? `&patient=` + props.patientId : ``) +
        (state.status ? `&status=` + state.status : `&status:not=cancelled`) +
        (state.priority !== null
          ? state.priority === 'routine'
            ? `&priority:not=asap`
            : `&priority=${state.priority}`
          : '') +
        (state.category
          ? `&code=` + state.category
          : `&code=touchpoint,survey,food-card,confirm-food-card-balance,other-goods-delivery,food-box,food-recall,food-delivery,education,consultation,careplan,communication,triage,outreach,voicemail,enrollment-action`) + //hide automated and member tasks by default
        (state.period ? (state.period.includes('ge') ? state.period.substring(0) : state.period) : ``) +
        (state.referral ? `&group-identifier=` + state.referral : ``) +
        (nextPage && nextPageURL ? `&_page_token=` + nextPageURL : ``);
      endPoints.push(url);
    });

    if (
      !(
        props.activeitem !== ACTIVITY_TAB.MY_CARETEAM_TASKS ||
        (props.activeitem === ACTIVITY_TAB.MY_CARETEAM_TASKS && myPatientIds.length > 0)
      )
    ) {
      endPoints = [];
    }
  }

  const { mutate: tasksMutate, isValidating: tasksLoading } = useSWR(
    endPoints.length > 0 ? [authState.token, ...endPoints] : null,
    MultiFetcher,
    options
  );

  const flatPractitioners = fhirPractitioners
    ? fhirPractitioners.map((fhirPractitioner: ResourceEntry<'Practitioner'>) =>
        FHIRUtils.parseFHIRPractitioner(fhirPractitioner.resource)
      )
    : [];

  // `parseFHIRPractitioner` builds every field through `fhirpath.evaluate(...).toString()`,
  // so `id` is a `string` here even where the underlying `Practitioner.id` was absent —
  // an id-less practitioner yields `''`, not `undefined`.
  const practlist = flatPractitioners.map((practitioner: FlatPractitioner) => ({
    key: practitioner.id,
    text: practitioner.fullName,
    value: practitioner.id.toString(),
  }));

  const _practDictionary = Object.assign(
    {},
    ...practlist.map((x: { key: string; text: string }) => ({ [x.key]: x.text })),
  ) as Record<string, string>;

  const handleSelectTask = (value?: string) => {
    setCurrentTaskId(value ?? null);
  };

  const handleSelectedTasks = (tasks: Task[]) => {
    setSelectedTasks(tasks);
  };

  const handleDisableMultiSelect = (value: boolean) => {
    setDisableMultiSelect(value);
  };

  const handleFilterTaskToggle = (value: boolean) => {
    setIsTaskFilterOpen(value);
  };

  const resetFilters = () => {
    setDateValue(null);
    setState({
      category: null,
      period: null,
      status: null,
      priority: null,
      practitioner: null,
      vendor: null,
      referral: null,
      subCategory: null,
      dateStatus: null,
      program: [],
    });
    setOriginalTaskList(null);
  };

  const toggleSidebar = () => {
    setSidebarVisible(!sidebarVisible);
  };

  const handleNewTask = (newTaskId: string) => {
    toggleSidebar();
    setNewTaskId(newTaskId);
    void tasksMutate();
    void props.patientTasksMutate?.();
  };

  return (
    <>
      <Grid columns={2} divided>
        <Grid.Column
          width={10}
          style={{ maxHeight: '80vh', minHeight: '70vh', display: 'flex', flexDirection: 'column' }}
        >
          <TaskHeader
            disableMultiSelect={disableMultiSelect}
            flatPractitioners={flatPractitioners}
            foodVendors={vendorList}
            handleNewTask={handleNewTask}
            isActivity={activityBool}
            orgCode={props.orgId}
            patientId={props.patientId}
            practitionerList={practlist}
            referralIdList={referralIdList}
            render={render}
            selectedTasks={selectedTasks}
            setRender={setRender}
            setRenderUrl={setRenderURL}
            setSelectedTasks={setSelectedTasks}
            taskCount={taskList?.length}
            tasksMutate={tasksMutate}
            selectedCareplanTasks={selectedCareplanTasks}
          >
            <TaskFilters
              patientId={props.patientId}
              practfilter={props.practfilter}
              setLoader={setLoader}
              practlist={practlist}
              state={state}
              setState={setState}
              datevalue={datevalue}
              setDateValue={setDateValue}
              resetFilters={resetFilters}
              referralIdList={referralIdList}
              activity={props.activity}
              foodVendor={vendorList}
              tasksMutate={tasksMutate}
              setRenderURL={setRenderURL}
              selectedPayerDropdown={props?.selectedPayer}
              activeitem={props.activeitem}
              retriveFilterFromLocalStorage={retriveFilterFromLocalStorage}
              handleFilterTaskToggle={handleFilterTaskToggle}
              isTaskFilterOpen={isTaskFilterOpen}
            />
          </TaskHeader>
          <div style={{ flex: 1, overflowY: 'auto' }}>
            <Segment style={{ margin: 0, border: 0, boxShadow: 'none' }}>
              {taskList && !loader && (
                <NewTaskList
                  currentTaskId={currentTaskId}
                  handleDisableMultiSelect={handleDisableMultiSelect}
                  handleSelectTask={handleSelectTask}
                  handleSelectedTasks={handleSelectedTasks}
                  selectedTasks={selectedTasks}
                  taskList={taskList}
                  fhirPatient={props.fhirPatient}
                />
              )}
              {(tasksLoading || careTeamDataLoading || loader) && (
                <Loader active inline="centered" content="Fetching Tasks" />
              )}
              {!taskList && !loader && <h2>No Tasks</h2>}
            </Segment>
          </div>
        </Grid.Column>
        <Grid.Column width={6} style={{ maxHeight: '80vh', minHeight: '70vh', overflow: 'auto' }}>
          <TaskDetail
            tasksMutate={tasksMutate}
            patientTasksMutate={props.patientTasksMutate}
            taskId={currentTaskId}
            practlist={practlist}
            taskList={taskList}
            flatPractitioners={flatPractitioners}
            onPatientSelected={props.onPatientSelected}
            activity={activityBool}
            patientId={props.patientId}
            render={render}
            setRenderURL={setRenderURL}
            setPreviousURL={setPreviousURL}
            foodVendor={vendorList}
          />
        </Grid.Column>
      </Grid>
    </>
  );
});

export default TaskCard;
